import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import {
  ExpandMoreRounded,
  FileDownloadRounded,
  InfoOutlined,
  PictureAsPdfRounded,
  SlideshowRounded,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { graduationCandidatesApi } from '../../../../lib/api/graduation-candidates.api';
import type { GraduationCandidates } from '../../../../lib/api/graduation-candidates.api';
import { errorText } from '../../../../lib/error-text';
import { useTenantStore } from '../../../../stores/tenant.store';
import { nearOptions } from './graduation-candidates.model';
import {
  buildCandidatesReport,
  reportCsv,
  type CandidatesReport,
  type CandidatesView,
  type ReportRow,
} from './candidates-report.model';
import { useCandidatesExports } from './use-candidates-slides';

// ─── Candidatos a graduación (expediente por credenciales) ──────────────────
// Los que ya cumplen los requisitos de un programa y los que están cerca de
// egresar, con el MISMO cálculo que el expediente de cada uno. No aparecen los
// ya egresados ni quien el sistema de origen registra como fallecido.
//
// PR 2 · Ajustes (PM, 2026-10-27): la página, el PDF carta, las diapositivas y
// el CSV salen del MISMO modelo (candidates-report). Cada uno cubre UNA vista
// («con estudios recientes» o «sin estudios recientes»), con el mismo total en
// el resumen, la distribución y la lista. La lista va numerada y agrupada por
// cursos que faltan; bajo cada nombre, lo que falta por tipo suma «Faltan».

export function GraduationCandidatesTab() {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [view, setView] = useState<CandidatesView>('recent');
  const query = useQuery({
    queryKey: ['graduation-candidates', currentTenantId],
    enabled: !!currentTenantId,
    queryFn: () => graduationCandidatesApi.list({ tenantId: currentTenantId! }),
  });
  // «Cerca» en pantalla: 4 · 3 · 2 · 1 cursos (Adrian). Arranca en el umbral del
  // seminario; la página, el CSV, la carta y las diapositivas usan la misma vista.
  const serverNear = query.data?.nearMaxCourses ?? null;
  const [nearSel, setNearSel] = useState<number | null>(null);
  useEffect(() => setNearSel(serverNear), [serverNear]);

  if (query.isError) return <Alert severity="error">{errorText(query.error)}</Alert>;
  if (!query.data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  const data = query.data;
  const near = nearSel ?? data.nearMaxCourses;
  const report = buildCandidatesReport(data, { view, near });
  const counts = {
    recent: buildCandidatesReport(data, { view: 'recent', near }).total,
    notRecent: buildCandidatesReport(data, { view: 'notRecent', near }).total,
  };
  const c = (key: string, o?: Record<string, unknown>) => t(`reports.candidates.${key}`, o ?? {});

  const exportCsv = () => {
    const csv = reportCsv(report, {
      n: c('columns.n'),
      group: c('columns.group'),
      studentCode: c('columns.studentCode'),
      lastName: c('columns.lastName'),
      firstName: c('columns.firstName'),
      program: c('columns.program'),
      missing: c('columns.missing'),
      required: c('columns.required'),
      core: c('columns.core'),
      electives: c('columns.electives'),
      units: c('columns.units'),
      lastTerm: c('columns.lastTerm'),
      inProgress: c('columns.inProgress'),
      groupLabel: (m) => (m === 0 ? c('eligible') : c('missingCourses', { count: m })),
    });
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${c(`csvFile.${view}`)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={3} sx={{ mt: 2 }}>
      <RulesSection report={report} />

      <Tabs
        value={view}
        onChange={(_, v: CandidatesView) => setView(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab value="recent" label={c('tab.recent', { count: counts.recent })} />
        <Tab value="notRecent" label={c('tab.notRecent', { count: counts.notRecent })} />
      </Tabs>
      {/* Franja de controles: UNA fila en escritorio; en pantallas angostas se
          parte en dos sin desbordar. La regla de la pestaña va en el ⓘ. */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" id="candidates-near-label">
            {c('nearLabel')}
          </Typography>
          {/* Selector, unidad e ⓘ no se separan al partirse la fila */}
          <Stack direction="row" alignItems="center" gap={1}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={near}
              onChange={(_, v: number | null) => v !== null && setNearSel(v)}
              aria-labelledby="candidates-near-label"
            >
              {nearOptions(data.nearMaxCourses).map((n) => (
                <ToggleButton key={n} value={n} aria-label={c('nearRule', { count: n })}>
                  {n}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary">
              {c('nearUnit', { count: near })}
            </Typography>
            <RuleInfo
              label={c('viewRuleInfo')}
              text={c(`viewRule.${view}`, { count: report.recentYears })}
            />
          </Stack>
        </Stack>
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ minWidth: 0 }}>
          <SlidesButton data={data} near={near} view={view} />
          <LetterButton data={data} near={near} view={view} />
          <Button
            variant="outlined"
            startIcon={<FileDownloadRounded />}
            onClick={exportCsv}
            disabled={report.total === 0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {c('exportCsv')}
          </Button>
        </Stack>
      </Stack>

      <Summary report={report} />
      <CandidatesList report={report} reviewGraduated={view === 'notRecent'} />
      <Typography variant="caption" color="text.secondary">
        {c('evaluatedRule')} {c('footnote')}
      </Typography>
    </Stack>
  );
}

/** Cierres automáticos que no aplican a un tooltip abierto con clic o toque. */
const PINNED_IGNORES = new Set(['mouseleave', 'touchend']);

/** La regla de la pestaña en un ⓘ: se abre al pasar el mouse, con el foco del
 *  teclado y con un toque (el toque de MUI pide mantener presionado; aquí basta
 *  uno). Se cierra solo cuando la persona lo cierra: al tocar fuera, con Esc o
 *  al perder el foco; nunca por tiempo. */
function RuleInfo({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  // Abierto con un clic o un toque, queda fijo: ni el «mouseleave» que el
  // navegador emite después de un toque ni el temporizador de MUI tras soltar
  // el dedo («touchend») lo cierran.
  const [pinned, setPinned] = useState(false);
  const close = () => {
    setOpen(false);
    setPinned(false);
  };
  return (
    <ClickAwayListener onClickAway={close}>
      <Tooltip
        title={text}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={(e) => (pinned && PINNED_IGNORES.has(e.type) ? undefined : close())}
        enterTouchDelay={0}
        describeChild
        arrow
        slotProps={{ tooltip: { sx: { fontSize: 13, maxWidth: 320 } } }}
      >
        <IconButton
          size="small"
          aria-label={label}
          onClick={() => {
            setOpen(true);
            setPinned(true);
          }}
        >
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  );
}

/** Resumen corto de una regla para el encabezado cerrado:
 *  programa · N obligatorios · núcleo X de Y · N créditos. */
function ruleSummary(
  rule: CandidatesReport['rules'][number],
  c: (key: string, o?: Record<string, unknown>) => string,
): string {
  return [
    rule.name,
    c('rules.summaryRequired', { count: rule.required.length }),
    ...rule.core.map((g) => c('rules.summaryCore', { min: g.minCourses, of: g.options.length })),
    c('rules.summaryCredits', { count: rule.minUnits }),
  ].join(' · ');
}

/** Las reglas de egreso de cada programa, UNA vez (las opciones del núcleo aquí,
 *  no en cada fila). */
function RulesSection({ report }: { report: CandidatesReport }) {
  const { t } = useTranslation('sis');
  const c = (key: string, o?: Record<string, unknown>) => t(`reports.candidates.${key}`, o ?? {});
  if (report.rules.length === 0) return null;
  const courses = (list: Array<{ code: string; name: string }>) =>
    list.map((x) => `${x.code} ${x.name}`.trim()).join(' · ');
  return (
    <Accordion variant="outlined" disableGutters defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{c('rules.title')}</Typography>
          {report.rules.map((rule) => (
            <Typography key={rule.ruleId} variant="body2" color="text.secondary">
              {ruleSummary(rule, c)}
            </Typography>
          ))}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1.5}>
          {report.rules.map((rule) => (
            <Box key={rule.ruleId}>
              <Typography variant="body2" fontWeight={700}>
                {c('rules.program', { name: rule.name })}
              </Typography>
              <Typography variant="body2">
                {`${c('rules.required', { count: rule.required.length })} ${courses(rule.required)}`}
              </Typography>
              {rule.core.map((g, i) => (
                <Typography key={i} variant="body2">
                  {`${c('rules.core', { count: g.minCourses })} ${courses(g.options)}`}
                </Typography>
              ))}
              <Typography variant="body2" color="text.secondary">
                {c('rules.credits', { min: rule.minUnits, per: rule.unitsPerCourse })}
              </Typography>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

function Summary({ report }: { report: CandidatesReport }) {
  const { t } = useTranslation('sis');
  const c = (key: string) => t(`reports.candidates.${key}`);
  const items: Array<[string, number]> = [
    [c('eligible'), report.summary.eligible],
    [c('near'), report.summary.near],
    [c('total'), report.summary.total],
    [c('evaluated'), report.summary.evaluated],
  ];
  return (
    <Stack direction="row" gap={2} flexWrap="wrap">
      {items.map(([label, value]) => (
        <Card
          key={label}
          variant="outlined"
          sx={{ borderRadius: 3, minWidth: 150, flex: '1 1 150px' }}
        >
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="h5" fontWeight={800}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

/** Lo que le falta a una persona, por tipo (suma «Faltan»). */
export function DetailLines({ row }: { row: ReportRow }) {
  const { t } = useTranslation('sis');
  const c = (key: string, o?: Record<string, unknown>) => t(`reports.candidates.${key}`, o ?? {});
  const lines = [
    row.required.length
      ? c('detail.required', {
          list: row.required.map((x) => `${x.code} ${x.name}`.trim()).join(', '),
        })
      : '',
    row.core ? c('detail.core', { count: row.core }) : '',
    row.electives ? c('detail.electives', { count: row.electives }) : '',
  ].filter(Boolean);
  return (
    <>
      {lines.map((l) => (
        <Typography key={l} variant="caption" color="text.secondary" display="block">
          {l}
        </Typography>
      ))}
    </>
  );
}

function CandidatesList({
  report,
  reviewGraduated,
}: {
  report: CandidatesReport;
  reviewGraduated: boolean;
}) {
  const { t } = useTranslation('sis');
  const c = (key: string, o?: Record<string, unknown>) => t(`reports.candidates.${key}`, o ?? {});
  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {c('listTitle')}
          </Typography>
          <Chip size="small" label={report.total} />
        </Stack>
        {report.total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {c('empty')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label={c('listTitle')}>
              <TableHead>
                <TableRow
                  sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: 12 } }}
                >
                  <TableCell sx={{ width: 40 }}>{c('columns.n')}</TableCell>
                  <TableCell>{c('columns.student')}</TableCell>
                  <TableCell>{c('columns.program')}</TableCell>
                  <TableCell align="center">{c('columns.missing')}</TableCell>
                  <TableCell align="center">{c('columns.units')}</TableCell>
                  <TableCell>{c('columns.lastTerm')}</TableCell>
                  <TableCell align="center">{c('columns.inProgress')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.groups.map((g) => (
                  <Fragment key={g.missing}>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" fontWeight={700}>
                          {`${g.missing === 0 ? c('eligible') : c('missingCourses', { count: g.missing })} · ${c('subtotal', { count: g.rows.length })}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {g.rows.map((r) => (
                      <TableRow key={`${r.personId}-${r.program}`} hover>
                        <TableCell sx={{ verticalAlign: 'top' }}>{r.n}</TableCell>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/people/${r.personId}`}
                            underline="hover"
                          >
                            <Typography variant="body2" fontWeight={600} component="span">
                              {`${r.lastName}, ${r.firstName}`.replace(/^, |, $/g, '')}
                            </Typography>
                          </Link>
                          {r.studentCode && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {r.studentCode}
                            </Typography>
                          )}
                          <DetailLines row={r} />
                          {reviewGraduated && r.eligible && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={c('reviewGraduated')}
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{r.program}</TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          {r.missing}
                        </TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          {r.unitsEarned} / {r.unitsRequired}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{r.lastTerm ?? '—'}</TableCell>
                        <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                          {r.inProgress || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

/** Diapositivas 16:9 para presentar. SIN nombres por defecto: la lista con
 *  nombres (datos personales) se agrega solo si quien exporta la marca. */
function SlidesButton({
  data,
  near,
  view,
}: {
  data: GraduationCandidates;
  near: number;
  view: CandidatesView;
}) {
  const { t } = useTranslation('sis');
  const [open, setOpen] = useState(false);
  const [includeNames, setIncludeNames] = useState(false);
  const { download, error } = useCandidatesExports(data, near);
  const k = (key: string, o?: Record<string, unknown>) =>
    t(`reports.candidates.slides.${key}`, o ?? {});

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<SlideshowRounded />}
        onClick={() => {
          setIncludeNames(false);
          setOpen(true);
        }}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('reports.export.slides')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{k('dialogTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              {k('dialogDesc', { view: t(`reports.candidates.views.${view}`) })}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  id="candidates-slides-names"
                  checked={includeNames}
                  onChange={(e) => setIncludeNames(e.target.checked)}
                />
              }
              label={k('includeNames')}
            />
            {includeNames && <Alert severity="warning">{k('includeNamesWarn')}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{k('cancel')}</Button>
          <Button variant="contained" onClick={() => void download({ includeNames, view })}>
            {k('generate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Informe carta con la lista CON nombres de la vista elegida (datos personales:
 *  la pestaña es solo de gestión, y cada página lo dice al pie). */
function LetterButton({
  data,
  near,
  view,
}: {
  data: GraduationCandidates;
  near: number;
  view: CandidatesView;
}) {
  const { t } = useTranslation('sis');
  const { downloadLetter, error } = useCandidatesExports(data, near);
  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PictureAsPdfRounded />}
        onClick={() => void downloadLetter(view)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('reports.export.letter')}
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
    </>
  );
}
